import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { GatsbyImage, type IGatsbyImageData } from 'gatsby-plugin-image';
import { type FC } from 'react';
import styled from 'styled-components';

import { media, theme } from 'src/styles';
import { DotButtons } from './dot-buttons';

type Props = {
  className?: string;
  images: readonly ({
    readonly gatsbyImageData: IGatsbyImageData | null;
  } | null)[];
};

export const Carousel: FC<Props> = ({ className, images }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ stopOnInteraction: false }),
  ]);
  const nonNullableImages = images.filter(
    (image) => image?.gatsbyImageData
  ) as { gatsbyImageData: IGatsbyImageData }[];
  return (
    <Wrapper className={`${className ?? ''}`}>
      {nonNullableImages.length < 3 ? (
        <GridImages>
          {nonNullableImages.map((image, index) => (
            <GridItem
              key={`history-image-${index}`}
              image={image.gatsbyImageData}
              alt=""
            />
          ))}
        </GridImages>
      ) : (
        <Embla className="embla">
          <EmblaViewport className="embla__viewport" ref={emblaRef}>
            <EmblaContainer className="embla__container">
              {nonNullableImages.map((image, index) => (
                <EmblaSlide
                  className="embla__slide"
                  key={`history-image-${index}`}
                >
                  <GatsbyImage image={image.gatsbyImageData} alt="" />
                </EmblaSlide>
              ))}
            </EmblaContainer>
          </EmblaViewport>
          <EmblaControls className="embla__controls">
            <StyledDotButtons
              className="embla__dots"
              length={nonNullableImages.length}
              emblaApi={emblaApi}
            />
          </EmblaControls>
        </Embla>
      )}
    </Wrapper>
  );
};

const EmblaSlide = styled.div`
  min-width: 0;
  flex: 0 0 var(--slide-size);
  aspect-ratio: 1;
  padding-left: var(--slide-spacing);
  & > div {
    height: 100%;
    width: 100%;
  }
`;

const EmblaContainer = styled.div`
  display: flex;
  margin-left: calc(var(--slide-spacing) * -1);
`;

const StyledDotButtons = styled(DotButtons)`
  margin-top: 20px;
`;

const EmblaControls = styled.div``;

const EmblaViewport = styled.div`
  overflow: hidden;
  ${media.lessThanIpadVertical`
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
  `}
`;

const Embla = styled.div`
  --slide-size: 52%;
  --slide-spacing: 3%;
`;

const GridItem = styled(GatsbyImage)`
  aspect-ratio: 1;
`;

const GridImages = styled.div`
  display: grid;
  ${media.ipadVerticalOrMore`
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  `}
`;

const Wrapper = styled.div`
  padding-top: ${theme.structure.margin.block.normal}px;
  padding-bottom: ${theme.structure.margin.block.xsmall}px;
`;

export default Carousel;
